// qastudyinterface.js - Student Q&A Study Interface
import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { API_BASE, authFetch } from '../services/api.js';
import './qastudyinterface.css';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeRaw from 'rehype-raw';
import { useEffect as useEffectMermaid } from 'react';
import PredefinedQADoubtChat from '../components/PredefinedQADoubtChat';
import CustomSelect from '../components/CustomSelect';
import ContentBlockRenderer from './ContentBlockRenderer';

const QAStudyInterface = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const RICH_CONTENT_TYPES = ['Accounts Numericals', 'Economics Numericals'];
  
  // Get preset filters if passed from other pages (for future use)
  const presetFilters = location.state || {};
  
  // State for hierarchy selection
  const [subjects, setSubjects] = useState([]);
  const [chapters, setChapters] = useState([]);
  const [topics, setTopics] = useState([]);
  const [subtopics, setSubtopics] = useState([]);
  const [questionTypes, setQuestionTypes] = useState([]);
  const [showDoubtChat, setShowDoubtChat] = useState(false);
  const [selectedQAForDoubt, setSelectedQAForDoubt] = useState(null);

  // Selected values
  const [selectedSubject, setSelectedSubject] = useState(presetFilters.subject_id || '');
  const [selectedChapter, setSelectedChapter] = useState(presetFilters.chapter_id || '');
  const [selectedTopic, setSelectedTopic] = useState(presetFilters.topic_id || '');
  const [selectedSubtopic, setSelectedSubtopic] = useState(presetFilters.subtopic_id || '');
  const [selectedQuestionType, setSelectedQuestionType] = useState('');
  const [selectedLanguage, setSelectedLanguage] = useState('en');
  
  // State for questions view
  const [showQuestions, setShowQuestions] = useState(false);
  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [hasMore, setHasMore] = useState(false);
  const [offset, setOffset] = useState(0);
  const [totalCount, setTotalCount] = useState(0);
  
  // State for UI
  const [expandedParents, setExpandedParents] = useState(new Set());
  const [expandedQuestions, setExpandedQuestions] = useState(new Set());
  const [expandedExplanations, setExpandedExplanations] = useState(new Set());
  
  // State for "Remember Selection" checkboxes
  const [rememberSubject, setRememberSubject] = useState(false);
  const [rememberChapter, setRememberChapter] = useState(false);
  const [rememberTopic, setRememberTopic] = useState(false);
  const [rememberSubtopic, setRememberSubtopic] = useState(false);
  const [rememberQuestionType, setRememberQuestionType] = useState(false);
  const [rememberLanguage, setRememberLanguage] = useState(false);
  // State for "Expand All" toggle
  const [expandAllChildren, setExpandAllChildren] = useState(false);
  const [expandedChildren, setExpandedChildren] = useState(new Set());

  // Check if student has doubt_resolution access for current subject
  const hasDoubtAccess = () => {
    const userData = JSON.parse(localStorage.getItem('userData'));
    const accessibleSubjects = userData?.student?.accessible_subjects || [];
    
    // Find current subject in accessible subjects
    const currentSubject = accessibleSubjects.find(
      sub => sub.subject_id === parseInt(selectedSubject)
    );
    
    // Check if doubt_resolution feature is enabled for this subject
    return currentSubject?.features?.includes('doubt_resolution') || false;
  };
  
  const openDoubtChat = (qa, isOneLiner = false) => {
    setSelectedQAForDoubt({
      qa_id: qa.qa_id,
      question: qa.question,
      answer: qa.answer,
      isOneLiner: isOneLiner,
      parent_id: isOneLiner ? qa.parent_id : null
    });
    setShowDoubtChat(true);
  };

  const closeDoubtChat = () => {
    setShowDoubtChat(false);
    setSelectedQAForDoubt(null);
  };
  
  // Load saved preferences from localStorage
  useEffect(() => {
    const savedPrefs = localStorage.getItem('qaStudyPreferences');
    if (savedPrefs) {
      try {
        const prefs = JSON.parse(savedPrefs);
        
        if (prefs.rememberSubject && prefs.subject) {
          setSelectedSubject(prefs.subject);
          setRememberSubject(true);
        }
        if (prefs.rememberChapter && prefs.chapter) {
          setSelectedChapter(prefs.chapter);
          setRememberChapter(true);
        }
        if (prefs.rememberTopic && prefs.topic) {
          setSelectedTopic(prefs.topic);
          setRememberTopic(true);
        }
        if (prefs.rememberSubtopic && prefs.subtopic) {
          setSelectedSubtopic(prefs.subtopic);
          setRememberSubtopic(true);
        }
        if (prefs.rememberQuestionType && prefs.questionType) {
          setSelectedQuestionType(prefs.questionType);
          setRememberQuestionType(true);
        }
        if (prefs.rememberLanguage && prefs.language) {
          setSelectedLanguage(prefs.language);
          setRememberLanguage(true);
        }
      } catch (err) {
        console.error('Error loading saved preferences:', err);
      }
    }
  }, []);

  // Save preferences to localStorage
  const savePreferences = () => {
    const prefs = {
      subject: rememberSubject ? selectedSubject : null,
      rememberSubject: rememberSubject,
      chapter: rememberChapter ? selectedChapter : null,
      rememberChapter: rememberChapter,
      topic: rememberTopic ? selectedTopic : null,
      rememberTopic: rememberTopic,
      subtopic: rememberSubtopic ? selectedSubtopic : null,
      rememberSubtopic: rememberSubtopic,
      questionType: rememberQuestionType ? selectedQuestionType : null,
      rememberQuestionType: rememberQuestionType,
      language: rememberLanguage ? selectedLanguage : null,
      rememberLanguage: rememberLanguage
    };
    
    localStorage.setItem('qaStudyPreferences', JSON.stringify(prefs));
  };

  // Save whenever checkbox or selection changes
  useEffect(() => {
    savePreferences();
  }, [
    rememberSubject, selectedSubject,
    rememberChapter, selectedChapter,
    rememberTopic, selectedTopic,
    rememberSubtopic, selectedSubtopic,
    rememberQuestionType, selectedQuestionType,
    rememberLanguage, selectedLanguage
  ]);


  // Get student ID from localStorage
  const userData = localStorage.getItem('userData');
  const studentId = userData ? JSON.parse(userData)?.student?.student_id : null;
  
  // Load subjects on mount
  useEffect(() => {
    loadSubjects();
  }, []);
  
  
  
  // Load chapters when subject changes
  useEffect(() => {
    if (selectedSubject) {
      loadChapters(selectedSubject);
    } else {
      setChapters([]);
      setTopics([]);
      setSubtopics([]);
      setQuestionTypes([]);
    }
  }, [selectedSubject]);
  
  // Load topics when chapter changes
  useEffect(() => {
    if (selectedChapter) {
      loadTopics(selectedChapter);
    } else {
      setTopics([]);
      setSubtopics([]);
      setQuestionTypes([]);
    }
  }, [selectedChapter]);
  
  // Load subtopics when topic changes
  useEffect(() => {
    if (selectedTopic) {
      loadSubtopics(selectedTopic);
    } else {
      setSubtopics([]);
    }
  }, [selectedTopic]);

  // Load question types when subject changes (or any filter changes)
  useEffect(() => {
    if (selectedSubject) {
      loadQuestionTypes();
    } else {
      setQuestionTypes([]);
    }
  }, [selectedSubject, selectedChapter, selectedTopic, selectedSubtopic]);
  
  // Reload question types when subtopic changes
  useEffect(() => {
    if (selectedTopic) {
      loadQuestionTypes();
    }
  }, [selectedSubtopic]);
  
  
  // Initialize Mermaid for diagram rendering
  useEffectMermaid(() => {
    import('mermaid').then((mermaid) => {
      mermaid.default.initialize({
        startOnLoad: true,
        theme: 'default',
        securityLevel: 'loose',
        fontFamily: 'Arial, sans-serif'
      });
    });
  }, []);
  
  // Mermaid Diagram Component
  const MermaidDiagram = ({ chart }) => {
    const [svg, setSvg] = React.useState('');
    const elementId = React.useRef(`mermaid-${Math.random().toString(36).substr(2, 9)}`);
    
    React.useEffect(() => {
      if (chart) {
        import('mermaid').then((mermaid) => {
          mermaid.default.render(elementId.current, chart).then((result) => {
            setSvg(result.svg);
          }).catch((error) => {
            console.error('Mermaid rendering error:', error);
            setSvg(`<div style="color: red;">Error rendering diagram: ${error.message}</div>`);
          });
        });
      }
    }, [chart]);
    
    return (
      <div 
        className="mermaid-container"
        style={{
          margin: '12px 0',
          padding: '12px',
          background: '#f9fafb',
          borderRadius: '6px',
          border: '1px solid #e5e7eb',
          overflowX: 'auto'
        }}
        dangerouslySetInnerHTML={{ __html: svg }}
      />
    );
  };


  // API calls
  const loadSubjects = async () => {
    try {
      const response = await authFetch(`${API_BASE}/subjects/?student_id=${studentId}`);
      const data = await response.json();
      setSubjects(data);
    } catch (err) {
      console.error('Error loading subjects:', err);
    }
  };
  
  const loadChapters = async (subjectId) => {
    try {
      const response = await authFetch(`${API_BASE}/chapters/?subject_id=${subjectId}&student_id=${studentId}`);
      const data = await response.json();
      setChapters(data);
    } catch (err) {
      console.error('Error loading chapters:', err);
    }
  };
  
  const loadTopics = async (chapterId) => {
    try {
      const response = await authFetch(`${API_BASE}/topics/?chapter_id=${chapterId}&student_id=${studentId}`);
      const data = await response.json();
      setTopics(data);
    } catch (err) {
      console.error('Error loading topics:', err);
    }
  };
  
  const loadSubtopics = async (topicId) => {
    try {
      const response = await authFetch(`${API_BASE}/subtopics/?topic_id=${topicId}&student_id=${studentId}`);
      const data = await response.json();
      setSubtopics(data);
    } catch (err) {
      console.error('Error loading subtopics:', err);
    }
  };
  
  const loadQuestionTypes = async () => {
    try {
      const params = new URLSearchParams({
        subject_id: selectedSubject,
        student_id: studentId
      });
      
      // Only add if selected
      if (selectedChapter) {
        params.append('chapter_id', selectedChapter);
      }
      if (selectedTopic) {
        params.append('topic_id', selectedTopic);
      }
      if (selectedSubtopic) {
        params.append('subtopic_id', selectedSubtopic);
      }
      
      const response = await authFetch(`${API_BASE}/student-qa-study/question-types?${params}`);
      const data = await response.json();
      setQuestionTypes(data.available_types || []);
    } catch (err) {
      console.error('Error loading question types:', err);
      setQuestionTypes([]);
    }
  };
  
  const loadQuestions = async (resetOffset = false) => {
    if (!selectedSubject || !selectedQuestionType) {
      setError('Please select Subject and Question Type');
      return;
    }
    
    setLoading(true);
    setError(null);
    
    try {
      const currentOffset = resetOffset ? 0 : offset;
      
      const params = new URLSearchParams({
        subject_id: selectedSubject,
        question_type: selectedQuestionType,
        language: selectedLanguage,
        limit: 20,
        offset: currentOffset,
        student_id: studentId
      });
      
      // Only add if selected
      if (selectedChapter) {
        params.append('chapter_id', selectedChapter);
      }
      if (selectedTopic) {
        params.append('topic_id', selectedTopic);
      }
      if (selectedSubtopic) {
        params.append('subtopic_id', selectedSubtopic);
      }
      
      const response = await authFetch(`${API_BASE}/student-qa-study/questions?${params}`);
      
      if (!response.ok) {
        throw new Error('Failed to load questions');
      }
      
      const data = await response.json();
      
      if (resetOffset) {
        setQuestions(data.questions);
        setOffset(20);
      } else {
        setQuestions(prev => [...prev, ...data.questions]);
        setOffset(prev => prev + 20);
      }
      
      setHasMore(data.has_more);
      setTotalCount(data.total_count);
      setShowQuestions(true);
      
    } catch (err) {
      console.error('Error loading questions:', err);
      setError('Failed to load questions. Please try again.');
    } finally {
      setLoading(false);
    }
  };
  
  // UI Handlers
  const handleStartStudy = () => {
    setOffset(0);
    loadQuestions(true);
  };
  
  const handleLoadMore = () => {
    loadQuestions(false);
  };
  
  const handleBackToSelection = () => {
    setShowQuestions(false);
    setQuestions([]);
    setOffset(0);
    setExpandedParents(new Set());
    setExpandedQuestions(new Set());
    setExpandedExplanations(new Set());
    setExpandedChildren(new Set());  // NEW: Clear child expansions
    setExpandAllChildren(false);      // NEW: Reset toggle
  };
  
  const toggleParent = (parentId) => {
    const newExpanded = new Set();
    // If clicking on already expanded parent, close it
    // Otherwise, close all others and open only the clicked one
    if (!expandedParents.has(parentId)) {
      newExpanded.add(parentId);
    }
    setExpandedParents(newExpanded);
    
    // Also clear expanded children when switching parents
    setExpandedChildren(new Set());
    setExpandedExplanations(new Set());
  };
  
  const toggleQuestion = (qaId) => {
    const newExpanded = new Set(expandedQuestions);
    // Close all others, open only clicked one (accordion behavior)
    if (newExpanded.has(qaId)) {
      newExpanded.delete(qaId);
    } else {
      newExpanded.clear();
      newExpanded.add(qaId);
    }
    setExpandedQuestions(newExpanded);
  };
  
  const toggleExplanation = (qaId) => {
    const newExpanded = new Set(expandedExplanations);
    if (newExpanded.has(qaId)) {
      newExpanded.delete(qaId);
    } else {
      newExpanded.add(qaId);
    }
    setExpandedExplanations(newExpanded);
  };

  const toggleChild = (childQaId) => {
    // If expandAllChildren is enabled, don't use accordion behavior
    if (expandAllChildren) {
      return; // Do nothing, they're all expanded anyway
    }
    
    const newExpanded = new Set();
    // Accordion behavior: close all others, open only clicked one
    // If clicking already expanded child, close it
    if (!expandedChildren.has(childQaId)) {
      newExpanded.add(childQaId);
    }
    setExpandedChildren(newExpanded);
    
    // Also clear explanations when switching children
    setExpandedExplanations(new Set());
  };


  
  // Auto-detect if text contains markdown syntax
const hasMarkdown = (text) => {
  if (!text) return false;
  
  // Check for common markdown patterns
  const markdownPatterns = [
    /\*\*.*\*\*/,        // Bold: **text**
    /__.*__/,            // Bold: __text__
    /\*.*\*/,            // Italic: *text*
    /_.*_/,              // Italic: _text_
    /^#{1,6}\s/m,        // Headers: # ## ###
    /^\-\s/m,            // Unordered list: - item
    /^\*\s/m,            // Unordered list: * item
    /^\d+\.\s/m,         // Ordered list: 1. item
    /\[.*\]\(.*\)/,      // Links: [text](url)
    /```[\s\S]*```/,     // Code blocks
    /`.*`/               // Inline code: `code`
  ];
  
  return markdownPatterns.some(pattern => pattern.test(text));
};
  
  
  // Format answer with markdown support or line breaks
  // Format answer based on format type and content
  const formatAnswer = (text, formatType = 'plain') => {
  if (!text) return null;
  
  // STEP 1: Replace ~ with double line breaks for proper paragraph separation
    const processedText = text.replace(/~/g, '\n\n');
    
    // STEP 2: If explicitly markdown or auto-detected markdown
    if (formatType === 'markdown' || hasMarkdown(processedText)) {
      return (
        <ReactMarkdown
          remarkPlugins={[remarkGfm]}
          rehypePlugins={[rehypeRaw]}
          components={{
            h1: ({node, ...props}) => <h1 style={{marginTop: '0', marginBottom: '8px'}} {...props} />,
            h2: ({node, ...props}) => <h2 style={{marginTop: '0', marginBottom: '8px'}} {...props} />,
            h3: ({node, ...props}) => <h3 style={{marginTop: '0', marginBottom: '8px'}} {...props} />,
            ol: ({node, ...props}) => <ol style={{marginTop: '6px', marginBottom: '8px', marginLeft: '20px', paddingLeft: '0'}} {...props} />,
            ul: ({node, ...props}) => <ul style={{marginTop: '6px', marginBottom: '8px', marginLeft: '20px', paddingLeft: '0'}} {...props} />,
            li: ({node, ...props}) => <li style={{marginBottom: '4px'}} {...props} />,
            p: ({node, ...props}) => <p style={{margin: '0 0 8px 0'}} {...props} />,
            table: ({node, ...props}) => (
              <div style={{overflowX: 'auto', marginBottom: '12px'}}>
                <table style={{borderCollapse: 'collapse', width: '100%', border: '1px solid #e5e7eb'}} {...props} />
              </div>
            ),
            th: ({node, ...props}) => <th style={{border: '1px solid #e5e7eb', padding: '8px', background: '#f3f4f6', textAlign: 'left'}} {...props} />,
            td: ({node, ...props}) => <td style={{border: '1px solid #e5e7eb', padding: '8px'}} {...props} />,
            code({node, inline, className, children, ...props}) {
              const match = /language-(\w+)/.exec(className || '');
              const language = match ? match[1] : '';
              
              // Check if it's a mermaid diagram
              if (language === 'mermaid') {
                return (
                  <MermaidDiagram chart={String(children).replace(/\n$/, '')} />
                );
              }
              
              // Regular code block
              if (!inline && match) {
                return (
                  <pre style={{
                    background: '#1f2937',
                    color: '#f3f4f6',
                    padding: '12px',
                    borderRadius: '6px',
                    overflowX: 'auto',
                    marginBottom: '12px'
                  }}>
                    <code className={className} {...props}>
                      {children}
                    </code>
                  </pre>
                );
              }
              
              // Inline code
              return (
                <code style={{
                  background: '#f3f4f6',
                  padding: '2px 6px',
                  borderRadius: '3px',
                  fontFamily: 'Courier New, monospace',
                  fontSize: '0.9em'
                }} {...props}>
                  {children}
                </code>
              );
            }
          }}
        >
          {processedText}
        </ReactMarkdown>
      );
    }
    
    // STEP 3: Plain text with ~ converted to paragraphs
    return processedText.split('\n').map((line, idx) => {
      const trimmedLine = line.trim();
      
      if (!trimmedLine) {
        return <br key={idx} />;
      }
      
      // Detect bullet patterns
      const bulletPatterns = [
        { regex: /^[-•]\s(.+)/, style: 'disc' },           // - or •
        { regex: /^\d+\)\s(.+)/, style: 'decimal' },       // 1) 2)
        { regex: /^\d+\.\s(.+)/, style: 'decimal' },       // 1. 2.
        { regex: /^[a-z]\)\s(.+)/, style: 'lower-alpha' }, // a) b)
        { regex: /^[A-Z]\)\s(.+)/, style: 'upper-alpha' }, // A) B)
        { regex: /^[ivxIVX]+\)\s(.+)/, style: 'lower-roman' } // i) ii)
      ];
      
      // Check if line matches bullet pattern
      for (const pattern of bulletPatterns) {
        const match = trimmedLine.match(pattern.regex);
        if (match) {
          return (
            <div key={idx} style={{ 
              marginLeft: '20px', 
              marginBottom: '8px',
              listStyleType: pattern.style
            }}>
              {match[1]}
            </div>
          );
        }
      }
      
      // Regular paragraph
      return (
        <p key={idx} style={{ marginBottom: '12px', margin: 0 }}>
          {trimmedLine}
        </p>
      );
    });
  };  
  
  // Render one-liner parent questions
  const renderOneLinerQuestions = () => {
    return questions.map((parent, idx) => (
      <div key={parent.parent_id} className="qa-parent-card">
        {/* Parent Question Header */}
        <div 
          className="qa-parent-header"
          onClick={() => toggleParent(parent.parent_id)}
        >
          <div className="qa-serial">{idx + 1}</div>
          <div className="qa-parent-question">{parent.question_stem}</div>
          <div className="qa-expand-icon">
            {expandedParents.has(parent.parent_id) ? '▼' : '▶'}
          </div>
        </div>
        
        {/* Child Questions - Show when expanded */}
        {expandedParents.has(parent.parent_id) && (
          <div className="qa-children-container">
            {parent.child_questions.map((child) => {
              const isChildExpanded = expandAllChildren || expandedChildren.has(child.qa_id);
              
              return (
                <div key={child.qa_id} className="qa-child-card">
                  {/* Child Question - Now clickable */}
                  <div 
                    className="qa-child-question"
                    onClick={() => toggleChild(child.qa_id)}
                    style={{ cursor: 'pointer' }}
                  >
                    <span style={{ marginRight: '8px' }}>
                      {isChildExpanded ? '▼' : '▶'}
                    </span>
                    {child.question}
                  </div>
                  
                  {/* Answer - Show only when expanded */}
                      {isChildExpanded && (
                        <>
                          <div className="qa-child-answer-row">
                            <div className="qa-child-answer">
                              <strong>Answer:</strong> {child.answer}
                            </div>
                            
                            {/* Explanation Toggle Button */}
                            {child.detailed_explanation && (
                              <button 
                                className="qa-explanation-btn-inline"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  const newExpanded = new Set();
                                  if (!expandedExplanations.has(child.qa_id)) {
                                    newExpanded.add(child.qa_id);
                                  }
                                  setExpandedExplanations(newExpanded);
                                }}
                                title={expandedExplanations.has(child.qa_id) ? 'Hide Explanation' : 'View Explanation'}
                              >
                                ❓
                              </button>
                            )}
                            
                            {/* Ask AI Button - Only show if has access */}
                            {hasDoubtAccess() && (
                              <button 
                                className="qa-ask-ai-btn-inline"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  openDoubtChat(child, true);
                                }}
                                title="Ask AI about this answer"
                              >
                                🤖
                              </button>
                            )}
                          </div>
                      
                      {/* Explanation Content - Show when toggled */}
                      {expandedExplanations.has(child.qa_id) && child.detailed_explanation && (
                        <div className="qa-explanation-content">
                          <strong>Explanation: </strong>
                          <span className="qa-explanation-text">
                            {formatAnswer(child.detailed_explanation, child.explanation_format)}
                          </span>
                        </div>
                      )}
                    </>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    ));
  };
  
  // Render text questions (accordion style)
  const renderTextQuestions = () => {
    return questions.map((qa, idx) => (
      <div key={qa.qa_id} className="qa-text-card">
        {/* Question Header */}
        <div 
          className={`qa-text-header ${expandedQuestions.has(qa.qa_id) ? 'expanded' : ''}`}
          onClick={() => toggleQuestion(qa.qa_id)}
        >
          <div className="qa-serial">{idx + 1}</div>
          <div className="qa-text-question">{qa.question}</div>
          <div className="qa-expand-icon">
            {expandedQuestions.has(qa.qa_id) ? '▼' : '▶'}
          </div>
        </div>

        {/* Question rich content — shown with the question (always visible) */}
        {qa.has_rich_content && (qa.question_blocks || []).length > 0 && (
          <div className="qa-question-blocks">
            {qa.question_blocks.map((block, bi) => (
              <ContentBlockRenderer key={`q-${bi}`} block={block} onMediaClick={null} />
            ))}
          </div>
        )}
        
        {/* Answer - Show when expanded */}
        {expandedQuestions.has(qa.qa_id) && (
          <div className="qa-text-answer-container">
            {/* Quick Answer if available */}
            {qa.one_word_answer && (
              <div className="qa-quick-answer">
                <strong>💡 Quick Answer:</strong> {qa.one_word_answer}
              </div>
            )}
            
            {/* Main Answer */}
            <div className="qa-main-answer">
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                <strong>📝 Answer:</strong>
                {/* Ask AI Button - Only show if has access */}
                {hasDoubtAccess() && (
                  <button 
                    className="qa-ask-ai-btn"
                    onClick={(e) => {
                      e.stopPropagation();
                      openDoubtChat(qa, false);
                    }}
                    title="Ask AI about this answer"
                  >
                    🤖 Ask AI
                  </button>
                )}
              </div>
              <div className="qa-answer-content">
                {formatAnswer(qa.answer, qa.answer_format)}
              </div>
            </div>
            
            {/* Detailed Explanation */}
            {qa.detailed_explanation && (
              <div className="qa-detailed-explanation">
                <strong>📚 Detailed Explanation:</strong>
                <div className="qa-explanation-content">
                  {formatAnswer(qa.detailed_explanation, qa.explanation_format)}
                </div>
              </div>
            )}
            
            {/* Reference */}
            {qa.past_year_reference && (
              <div className="qa-reference">
                <strong>📌 Reference:</strong> {qa.past_year_reference}
              </div>
            )}

            {/* Solution rich content — shown after the text answer */}
            {qa.has_rich_content && (qa.solution_blocks || []).length > 0 && (
              <div className="qa-text-blocks">
                {qa.solution_blocks.map((block, bi) => (
                  <ContentBlockRenderer key={`s-${bi}`} block={block} onMediaClick={null} />
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    ));
  };
  
  // Render born-rich (numerical) questions — click the question to reveal the solution
  const renderNumericalQuestions = () => {
    return questions.map((qa, idx) => {
      const isOpen = expandedQuestions.has(qa.qa_id);
      const questionBlocks = qa.question_blocks || [];
      const solutionBlocks = qa.solution_blocks || [];

      return (
        <div key={qa.qa_id} className="qa-text-card">
          {/* Question header — serial + question on one line, click to toggle solution */}
          <div
            className={`qa-text-header ${isOpen ? 'expanded' : ''}`}
            style={{ display: 'block', cursor: 'default' }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '10px' }}>
              <div className="qa-serial">{idx + 1}</div>
            </div>
            <div className="qa-text-question" style={{ width: '100%' }}>
              {questionBlocks.length > 0 ? (
                questionBlocks.map((block, bi) => (
                  <ContentBlockRenderer key={bi} block={block} onMediaClick={null} />
                ))
              ) : (
                <div className="qa-empty-blocks">No question content.</div>
              )}
            </div>
            <div
              onClick={() => toggleQuestion(qa.qa_id)}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '6px',
                marginTop: '12px',
                padding: '10px',
                background: '#f3f4f6',
                borderRadius: '8px',
                cursor: 'pointer',
                fontWeight: 600,
                fontSize: '0.9rem',
                color: '#667eea'
              }}
            >
              {isOpen ? 'Hide Solution' : 'Show Solution'}
              <span>{isOpen ? '▼' : '▶'}</span>
            </div>
          </div>

          {/* Solution — revealed on click */}
          {isOpen && (
            <div className="qa-numerical-solution">
              {solutionBlocks.length > 0 ? (
                solutionBlocks.map((block, bi) => (
                  <ContentBlockRenderer key={bi} block={block} onMediaClick={null} />
                ))
              ) : (
                <div className="qa-empty-blocks">No solution content.</div>
              )}
            </div>
          )}
        </div>
      );
    });
  };
  
  
  // Main render
  if (showQuestions) {
  // Questions View
  return (
    <div className="qa-study-container">
      {/* Header with Back button */}
      <div className="qa-study-header">
        <button className="qa-back-btn" onClick={handleBackToSelection}>
          ← Back to Selection
        </button>
        <h2>Practice Questions</h2>
        <div className="qa-count">
          Showing {questions.length} of {totalCount}
        </div>
      </div>
      
      {/* NEW: Expand All Toggle - Only show for one-liner types */}
      {selectedQuestionType.includes('One-liner') && (
        <div className="qa-expand-toggle" style={{
          padding: '12px 20px',
          background: '#f3f4f6',
          borderRadius: '8px',
          marginBottom: '16px',
          display: 'flex',
          alignItems: 'center',
          gap: '10px'
        }}>
          <label style={{ 
            display: 'flex', 
            alignItems: 'center', 
            cursor: 'pointer',
            fontSize: '14px',
            fontWeight: '500'
          }}>
            <input
              type="checkbox"
              checked={expandAllChildren}
              onChange={(e) => setExpandAllChildren(e.target.checked)}
              style={{ 
                marginRight: '8px',
                width: '18px',
                height: '18px',
                cursor: 'pointer'
              }}
            />
            <span>Expand all child answers automatically</span>
          </label>
          <span style={{ 
            fontSize: '12px', 
            color: '#6b7280',
            marginLeft: 'auto'
          }}>
            {expandAllChildren ? '✓ Enabled' : '○ Disabled'}
          </span>
        </div>
      )}
        
        {/* Questions List */}
        <div className="qa-questions-list">
          {loading && questions.length === 0 ? (
            <div className="qa-loading">Loading questions...</div>
          ) : error ? (
            <div className="qa-error">{error}</div>
          ) : questions.length === 0 ? (
            <div className="qa-empty">No questions found for this selection</div>
          ) : (
            <>
              {RICH_CONTENT_TYPES.includes(selectedQuestionType)
                ? renderNumericalQuestions()
                : selectedQuestionType.includes('One-liner')
                ? renderOneLinerQuestions()
                : renderTextQuestions()}
              
              {/* Load More Button */}
              {hasMore && (
                <button 
                  className="qa-load-more-btn"
                  onClick={handleLoadMore}
                  disabled={loading}
                >
                  {loading ? 'Loading...' : 'Load More Questions'}
                </button>
              )}
            </>
          )}
        </div>
        {/* ADD THIS SECTION HERE - Floating Doubt Chat */}
      {showDoubtChat && selectedQAForDoubt && (
        <PredefinedQADoubtChat
          qaId={selectedQAForDoubt.qa_id}
          subjectId={parseInt(selectedSubject)}
          chapterId={selectedChapter ? parseInt(selectedChapter) : null}
          topicId={selectedTopic ? parseInt(selectedTopic) : null}
          subtopicId={selectedSubtopic ? parseInt(selectedSubtopic) : null}
          language={selectedLanguage}
          onClose={closeDoubtChat}
        />
      )}
      {/* END OF FLOATING CHAT */}  
      
      
      </div>
    );
  }
  
  
  
  
  
  
  
  // Selection View
  return (
    <div className="qa-study-container">
      {/* Header */}
      <div className="qa-study-header">
        <button className="qa-back-btn" onClick={() => navigate('/dashboard')}>
          ← Back to Dashboard
        </button>
        <h2>Practice Q&A</h2>
      </div>
      
      {/* Selection Form */}
      <div className="qa-selection-form">
        <h3>Select Topic & Question Type</h3>
        
        {/* Subject */}
        <div className="qa-form-group">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <label>Subject *</label>
            <label style={{ fontSize: '0.85rem', fontWeight: 'normal' }}>
              <input
                type="checkbox"
                checked={rememberSubject}
                onChange={(e) => setRememberSubject(e.target.checked)}
                style={{ marginRight: '5px' }}
              />
              Remember
            </label>
          </div>
          <CustomSelect
            value={selectedSubject}
            onChange={(e) => {
              setSelectedSubject(e.target.value);
              setSelectedChapter('');
              setSelectedTopic('');
              setSelectedSubtopic('');
              setSelectedQuestionType('');
            }}
            placeholder="Choose Subject..."
            options={subjects.map(subject => ({ value: subject.subject_id, label: subject.name }))}
          />
        </div>
        
        {/* Chapter */}
        {selectedSubject && (
          <div className="qa-form-group">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <label>Chapter (Optional)</label>
              <label style={{ fontSize: '0.85rem', fontWeight: 'normal' }}>
                <input
                  type="checkbox"
                  checked={rememberChapter}
                  onChange={(e) => setRememberChapter(e.target.checked)}
                  style={{ marginRight: '5px' }}
                />
                Remember
              </label>
            </div>
            <CustomSelect
              value={selectedChapter}
              onChange={(e) => {
                setSelectedChapter(e.target.value);
                setSelectedTopic('');
                setSelectedSubtopic('');
                setSelectedQuestionType('');
              }}
              placeholder="Choose Chapter..."
              options={chapters.map(chapter => ({ value: chapter.chapter_id, label: chapter.name }))}
            />
          </div>
        )}
        
        {/* Topic */}
        {selectedSubject && (
          <div className="qa-form-group">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <label>Topic (Optional)</label>
              <label style={{ fontSize: '0.85rem', fontWeight: 'normal' }}>
                <input
                  type="checkbox"
                  checked={rememberTopic}
                  onChange={(e) => setRememberTopic(e.target.checked)}
                  style={{ marginRight: '5px' }}
                />
                Remember
              </label>
            </div>
            <CustomSelect
              value={selectedTopic}
              onChange={(e) => {
                setSelectedTopic(e.target.value);
                setSelectedSubtopic('');
                setSelectedQuestionType('');
              }}
              placeholder="Choose Topic..."
              options={topics.map(topic => ({ value: topic.topic_id, label: topic.name }))}
            />
          </div>
        )}
        
        {/* Subtopic */}
        {selectedSubject && subtopics.length > 0 && (
          <div className="qa-form-group">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <label>Subtopic (Optional)</label>
              <label style={{ fontSize: '0.85rem', fontWeight: 'normal' }}>
                <input
                  type="checkbox"
                  checked={rememberSubtopic}
                  onChange={(e) => setRememberSubtopic(e.target.checked)}
                  style={{ marginRight: '5px' }}
                />
                Remember
              </label>
            </div>
            <CustomSelect
              value={selectedSubtopic}
              onChange={(e) => {
                setSelectedSubtopic(e.target.value);
                setSelectedQuestionType('');
              }}
              placeholder="All Subtopics"
              options={subtopics.map(subtopic => ({ value: subtopic.subtopic_id, label: subtopic.name }))}
            />
          </div>
        )}
        
        {/* Question Type */}
        {selectedSubject && (
          <div className="qa-form-group">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <label>Question Type *</label>
              <label style={{ fontSize: '0.85rem', fontWeight: 'normal' }}>
                <input
                  type="checkbox"
                  checked={rememberQuestionType}
                  onChange={(e) => setRememberQuestionType(e.target.checked)}
                  style={{ marginRight: '5px' }}
                />
                Remember
              </label>
            </div>
            <CustomSelect
              value={selectedQuestionType}
              onChange={(e) => setSelectedQuestionType(e.target.value)}
              placeholder="Choose Question Type..."
              options={questionTypes.map(type => ({ value: type, label: type }))}
            />
          </div>
        )}
        
        {/* Language */}
        <div className="qa-form-group">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <label>Language</label>
            <label style={{ fontSize: '0.85rem', fontWeight: 'normal' }}>
              <input
                type="checkbox"
                checked={rememberLanguage}
                onChange={(e) => setRememberLanguage(e.target.checked)}
                style={{ marginRight: '5px' }}
              />
              Remember
            </label>
          </div>
          <CustomSelect
            value={selectedLanguage}
            onChange={(e) => setSelectedLanguage(e.target.value)}
            placeholder="Select Language"
            options={[
              { value: 'en', label: '🇺🇸 English' },
              { value: 'hi', label: '🇮🇳 Hindi' }
            ]}
          />
        </div>
        
        {/* Error Message */}
        {error && <div className="qa-error">{error}</div>}
        
        {/* Start Button */}
        <button 
          className="qa-start-btn"
          onClick={handleStartStudy}
          disabled={!selectedSubject || !selectedQuestionType || loading}
        >
          {loading ? 'Loading...' : 'Start Practice'}
        </button>
      </div>
    </div>
  );
};

export default QAStudyInterface;